import {
    all,
    call,
    put,
    select,
    takeLatest,
} from 'redux-saga/effects'

import { ShiftPlanningWithShift } from '../../@paco/entities/ShiftPlanning/ShiftPlanning'
import { getShiftPlanningsWithShiftApiCall } from '../../@paco/entities/ShiftPlanning/ShiftPlanningService'
import checkPermission from '../../@paco/helpers/permissions/checkPermission'
import { getCheckedFormOptionsValue } from '../../@paco/helpers/types/formOption'
import { shiftPlanningSlice } from '../../@paco/redux/shiftPlanning/shiftPlanningReducer'

import {
    LEAVE_OF_ABSENCES_REQUEST_APPROVED,
    LEAVE_OF_ABSENCES_REQUEST_CLOSED,
    LEAVE_OF_ABSENCES_REQUEST_OPEN,
    LIST_VIEW,
    PAGE_SIZES,
    TOAST_TYPE_PASS,
} from '../../constants'
import { getUserFullName } from '../../helpers'
import { getLeaveOfAbsenceRequestIncludes } from '../../helpers/api/absencesApi'
import { getPreferToWorkTimeSlots, getUnavailableWorkTimeSlots } from '../../helpers/api/shiftsApi'
import { getAbsencesInDateRangeLegacy } from '../../helpers/api-ts/absences'
import { getLeaveOfAbsencesLegacy, leaveOfAbsenceStatus } from '../../helpers/api-ts/leaveOfAbsence'
import { postShiftPlanning } from '../../helpers/api-ts/shiftPlanning'
import { getUsers } from '../../helpers/api-ts/userWithAvailability'
import { getEndOfWorkDay, getStartOfWorkDay } from '../../helpers/date'
import { translate } from '../../helpers/translations/translator'
import {
    BasicUser,
    DataWithPagination,
} from '../../models'
// eslint-disable-next-line import/no-cycle
import { deductOneSecondFromLOARequests } from '../absences-ts/absencesHelpers'
import {
    APP_SAGA_ADD_TOAST,
    SHIFTS_GET_AVAILABILITY_FAILURE,
    SHIFTS_GET_AVAILABILITY_REQUEST,
    SHIFTS_GET_AVAILABILITY_SUCCESS,
    SHIFTS_GET_USERS_WITH_AVAILABILITY_FAILURE,
    SHIFTS_GET_USERS_WITH_AVAILABILITY_REQUEST,
    SHIFTS_GET_USERS_WITH_AVAILABILITY_SUCCESS,
    SHIFTS_PLAN_AVAILABILITY_USER_TO_SHIFT_FAILURE,
    SHIFTS_PLAN_AVAILABILITY_USER_TO_SHIFT_REQUEST,
    SHIFTS_PLAN_AVAILABILITY_USER_TO_SHIFT_SUCCESS,
    SHIFTS_SAGA_GET_AVAILABILITY,
    SHIFTS_SAGA_GET_USERS_WITH_AVAILABILITY,
    SHIFTS_SAGA_PLAN_AVAILABILITY_USER_TO_SHIFT,
    SHIFTS_UPDATE_USERS_WITH_AVAILABILITY,
} from '../actionTypes'
import { setPagination } from '../app/appSaga'
import { Reducers } from '../reducers'
import removeShiftPlanningFromUsersWithAvailability from './helpers/removeShiftPlanningFromUsersWithAvailability'
import transformAvailabilityDataToUserWithAvailability from './helpers/transformAvailabilityDataToUserWithAvailability'
import updateShiftPlanningInUsersWithAvailability from './helpers/updateShiftPlanningInUsersWithAvailability'
import { GetUsersWithAvailabilityAction, PlanAvailabilityUserToShiftAction } from './shiftsModels'

export function* getAvailability(): any {
    yield put({ type: SHIFTS_GET_AVAILABILITY_REQUEST })
    try {
        const state: Reducers = yield select()
        const {
            filterReducer,
            authenticatedUserReducer,
            weekNavigatorReducer,
            globalFiltersReducer,
        } = state
        const { availability, employeeSearch } = filterReducer.filter
        const { departmentOptions } = globalFiltersReducer
        const { startDate, endDate } = weekNavigatorReducer
        const {
            available,
            unavailable,
            approvedLeaveOfAbsence,
            openLeaveOfAbsence,
            deniedLeaveOfAbsence,
            absence,
        } = availability

        const { user, permissions } = authenticatedUserReducer
        const { departments: userDepartments = [] } = user || {}
        const filteredDepartmentIds = getCheckedFormOptionsValue(departmentOptions)
        const userDepartmentIds = userDepartments.map(department => department.id)
        const departments = filteredDepartmentIds.length > 0 ? filteredDepartmentIds : userDepartmentIds

        const status = [
            ...(openLeaveOfAbsence ? [LEAVE_OF_ABSENCES_REQUEST_OPEN] : []),
            ...(approvedLeaveOfAbsence ? [LEAVE_OF_ABSENCES_REQUEST_APPROVED] : []),
            ...(deniedLeaveOfAbsence ? [LEAVE_OF_ABSENCES_REQUEST_CLOSED] : []),
        ] as leaveOfAbsenceStatus[]
        const leaveOfAbsence = openLeaveOfAbsence || approvedLeaveOfAbsence || deniedLeaveOfAbsence

        const canViewAbsences = checkPermission(permissions, 'view-all-absences')
        const canViewLeaveOfAbsences = checkPermission(permissions, 'view-all-leave-of-absences')
        const canViewAllPreferToWorkTimeSlots = checkPermission(permissions, 'view-all-prefer-to-work-time-slots')
        const canViewUnavailableToWorkTimeSlots = checkPermission(permissions, 'view-unavailable-to-work-time-slots')

        const [
            preferToWorkTimeSlots,
            unavailableToWorkTimeSlots,
            absences,
            leaveOfAbsences,
        ] = yield all([
            ...[(available && canViewAllPreferToWorkTimeSlots) ? getPreferToWorkTimeSlots(
                startDate,
                endDate,
                [],
                { departments, search: employeeSearch },
            ) : { data: [] }],
            ...[(unavailable && canViewUnavailableToWorkTimeSlots) ? getUnavailableWorkTimeSlots(
                startDate,
                endDate,
                [],
                { departments, search: employeeSearch },
            ) : { data: [] }],
            ...[(absence && canViewAbsences) ? getAbsencesInDateRangeLegacy(
                startDate,
                endDate,
                { departments, search: employeeSearch },
                undefined,
                [
                    'user',
                    'user.experience',
                    'user.roles',
                    'comments',
                    'absenceHours',
                    'absenceHours.payrollPeriod',
                ],
            ) : { data: [] }],
            ...[(leaveOfAbsence && canViewLeaveOfAbsences) ? getLeaveOfAbsencesLegacy({
                startDate,
                endDate,
                status,
                filter: { departments, search: employeeSearch },
                includes: [
                    'user',
                    'user.experience',
                    'user.person',
                    'user.roles',
                    'comments',
                    'leaveOfAbsenceHours',
                    'leaveOfAbsenceHours.payrollPeriod',
                ],
            }) : { data: [] }],
        ])

        const leaveOfAbsencesWithUpToAndIncludingDate = deductOneSecondFromLOARequests(leaveOfAbsences.data)

        yield put({
            preferToWorkTimeSlots: preferToWorkTimeSlots.data,
            unavailableToWorkTimeSlots: unavailableToWorkTimeSlots.data,
            absences: absences.data,
            leaveOfAbsences: leaveOfAbsencesWithUpToAndIncludingDate,
            type: SHIFTS_GET_AVAILABILITY_SUCCESS,
        })
    } catch (error) {
        yield put({ type: SHIFTS_GET_AVAILABILITY_FAILURE })
    }
}

export function* getUsersWithAvailability(action: GetUsersWithAvailabilityAction): any {
    yield put({ type: SHIFTS_GET_USERS_WITH_AVAILABILITY_REQUEST })

    try {
        const state: Reducers = yield select()
        const {
            calendarReducer,
            filterReducer,
            paginationReducer,
            authenticatedUserReducer,
            globalFiltersReducer,
        } = state
        const {
            availability,
            employeeSearch,
            onlyShowMainDepartment,
        } = filterReducer.filter
        const { departmentOptions } = globalFiltersReducer
        const { selectedDays } = calendarReducer
        const {
            available,
            unavailable,
            approvedLeaveOfAbsence,
            openLeaveOfAbsence,
            deniedLeaveOfAbsence,
            absence,
            sortByExperience,
        } = availability
        const { departments: userDepartments } = authenticatedUserReducer

        const filteredDepartmentIds = getCheckedFormOptionsValue(departmentOptions)
        const userDepartmentIds = userDepartments.map(department => department.id)
        const departments = filteredDepartmentIds.length > 0 ? filteredDepartmentIds : userDepartmentIds

        const {
            canViewAbsences,
            canViewLeaveOfAbsences,
        } = action

        const startDate = getStartOfWorkDay(selectedDays[0])
        const endDate = getEndOfWorkDay(selectedDays[selectedDays.length - 1])

        const { data: users, pagination } = yield getUsers({
            sortByExperience,
            workweek: startDate,
            filter: { departments, onlyShowMainDepartment, search: employeeSearch },
            pagination: { number: paginationReducer.number, size: PAGE_SIZES.SHIFTS.LIST_VIEW },
            includes: [
                'person',
                'departments',
                'mainDepartment',
                'experience',
                'employmentType',
            ],
        }) as unknown as DataWithPagination<BasicUser[]>
        const ids = users.map((user: BasicUser) => user.id)

        const status = [
            ...(openLeaveOfAbsence ? [LEAVE_OF_ABSENCES_REQUEST_OPEN] : []),
            ...(approvedLeaveOfAbsence ? [LEAVE_OF_ABSENCES_REQUEST_APPROVED] : []),
            ...(deniedLeaveOfAbsence ? [LEAVE_OF_ABSENCES_REQUEST_CLOSED] : []),
        ] as leaveOfAbsenceStatus[]

        const [
            preferToWorkTimeSlots,
            unavailableToWorkTimeSlots,
            absences,
            leaveOfAbsences,
            shiftPlannings,
        ] = yield all([
            ...[available ? getPreferToWorkTimeSlots(startDate, endDate, ids) : { data: [] }],
            ...[unavailable ? getUnavailableWorkTimeSlots(startDate, endDate, ids) : { data: [] }],
            ...[(absence && canViewAbsences) ? getAbsencesInDateRangeLegacy(
                getStartOfWorkDay(startDate),
                getEndOfWorkDay(endDate),
                { userIds: ids },
                undefined,
                [
                    'user',
                    'user.experience',
                    'user.roles',
                    'comments',
                    'absenceHours',
                    'absenceHours.payrollPeriod',
                ],
            ) : { data: [] }],
            ...[(status.length && canViewLeaveOfAbsences) ? getLeaveOfAbsencesLegacy({
                startDate: getStartOfWorkDay(startDate),
                endDate: getEndOfWorkDay(endDate),
                filter: { userIds: ids },
                status,
                includes: getLeaveOfAbsenceRequestIncludes(LIST_VIEW),
            }) : { data: [] }],
            getShiftPlanningsWithShiftApiCall({
                startDate: getStartOfWorkDay(startDate),
                endDate: getEndOfWorkDay(endDate),
                userUuids: ids,
            }),
        ])

        const usersWithAvailability = users
            .map((user: BasicUser) => transformAvailabilityDataToUserWithAvailability(
                user,
                absences.data,
                deductOneSecondFromLOARequests(leaveOfAbsences.data),
                preferToWorkTimeSlots.data,
                unavailableToWorkTimeSlots.data,
                shiftPlannings.data,
            ))

        yield call(() => setPagination({ pagination }))
        yield put({
            type: SHIFTS_GET_USERS_WITH_AVAILABILITY_SUCCESS,
            usersWithAvailability,
            page: pagination.number,
        })
    } catch (error) {
        console.error(error)
        yield put({ type: SHIFTS_GET_USERS_WITH_AVAILABILITY_FAILURE })
    }
}

export function* planAvailabilityUserToShift(action: PlanAvailabilityUserToShiftAction): any {
    yield put({ type: SHIFTS_PLAN_AVAILABILITY_USER_TO_SHIFT_REQUEST })

    try {
        const state: Reducers = yield select()
        const { authenticatedUserReducer } = state
        const { permissions } = authenticatedUserReducer

        const { shiftId, userId } = action
        const shiftPlanning = yield call(() => postShiftPlanning({ shiftId, userId }))
        const userName = getUserFullName(shiftPlanning.user)
        yield put({ type: SHIFTS_PLAN_AVAILABILITY_USER_TO_SHIFT_SUCCESS })
        yield put({
            type: APP_SAGA_ADD_TOAST,
            toast: translate('pages.shifts.userPlannedOnShift', { userName }),
            toastType: TOAST_TYPE_PASS,
        })

        const canViewAbsences = checkPermission(permissions, 'view-all-absences')
        const canViewLeaveOfAbsences = checkPermission(permissions, 'view-all-leave-of-absences')

        yield call(getUsersWithAvailability, {
            type: 'SHIFTS_SAGA_GET_USERS_WITH_AVAILABILITY',
            canViewAbsences,
            canViewLeaveOfAbsences,
        })
    } catch (error) {
        yield put({ type: SHIFTS_PLAN_AVAILABILITY_USER_TO_SHIFT_FAILURE, error })
    }
}

export function* onShiftPlanningUpdate(action: { type: 'shiftPlanningReducer/setShiftPlanning', payload: ShiftPlanningWithShift }): any {
    try {
        const state = yield select()
        const { shiftsReducer } = state
        const { usersWithAvailability } = shiftsReducer
        const updatedUsersWithAvailability = updateShiftPlanningInUsersWithAvailability(usersWithAvailability, action.payload)

        yield put({ type: SHIFTS_UPDATE_USERS_WITH_AVAILABILITY, usersWithAvailability: updatedUsersWithAvailability })
    } catch (error) {
        console.error(error)
    }
}

export function* onShiftPlanningDelete(action: { type: 'shiftPlanningReducer/setLastDeletedShiftPlanningId', payload: string }): any {
    try {
        const state = yield select()
        const { shiftsReducer } = state
        const { usersWithAvailability } = shiftsReducer
        const updatedUsersWithAvailability = removeShiftPlanningFromUsersWithAvailability(usersWithAvailability, action.payload)

        yield put({ type: SHIFTS_UPDATE_USERS_WITH_AVAILABILITY, usersWithAvailability: updatedUsersWithAvailability })
    } catch (error) {
        console.error(error)
    }
}

export default function* shiftsWatcher() {
    yield takeLatest(SHIFTS_SAGA_GET_AVAILABILITY, getAvailability)
    yield takeLatest(SHIFTS_SAGA_GET_USERS_WITH_AVAILABILITY, getUsersWithAvailability)
    yield takeLatest(SHIFTS_SAGA_PLAN_AVAILABILITY_USER_TO_SHIFT, planAvailabilityUserToShift)
    yield takeLatest(shiftPlanningSlice.actions.setShiftPlanning, onShiftPlanningUpdate)
    yield takeLatest(shiftPlanningSlice.actions.setLastDeletedShiftPlanningId, onShiftPlanningDelete)
}
